import { useState, useEffect } from "react";

const T = {
  black: "#111111",
  dark: "#1a1a1a",
  mid: "#555555",
  light: "#999999",
  border: "#ebebeb",
  borderHover: "#d0d0d0",
  cardBg: "#fafafa",
  radius: "16px",
  radiusSm: "12px",
};

const sections = [
  {
    id: "quick-help",
    title: "Quick Help",
    icon: "🔍",
    content: [
      {
        title: "Account & Login",
        body: "Help with accessing your account, email issues, or verification problems. If you're locked out, use the 'Forgot password' link on the login screen. For email changes, contact support@nudr.app with your registered email address.",
      },
      {
        title: "Safety & Reporting",
        body: "How to block, report users, and use the Red Flag system. Tap the menu icon on any profile to report or block. Every report is reviewed by our safety team, and accumulated Red Flags escalate enforcement up to permanent termination.",
      },
      {
        title: "Age Verification",
        body: "Why ID verification is required and exactly how it works. We require government ID to ensure everyone is 18+. Your ID is encrypted, processed by a third-party verifier, and never stored on our servers after verification.",
      },
      {
        title: "Privacy & Data",
        body: "How NudR protects your personal information and what we store. We never sell your data. You can request a full data export or account deletion from Settings > Privacy. All messages are end-to-end encrypted.",
      },
      {
        title: "Events & Meetups",
        body: "How real-world events work and who can attend. Only verified members can attend events. Each event has a host who checks IDs at the door. You must RSVP through the app to attend.",
      },
      {
        title: "NudR+ Membership",
        body: "Questions about premium access and exclusive features. NudR+ gives you unlimited matches, priority event access, profile boosts, and advanced privacy controls. Subscribe in the app for $9.99/month or $49.99/year.",
      },
    ],
  },
  {
    id: "red-flag",
    title: "🚩 Red Flag System",
    icon: "🚩",
    content: [
      {
        title: "What is a Red Flag?",
        body: "A Red Flag is a serious report submitted by another verified user. It's our community-driven safety system. Each flag requires a specific reason and evidence.",
      },
      {
        title: "What happens when you get Red Flags?",
        body: "Red Flags are applied by our safety team after reviewing a report. Accumulated flags escalate enforcement — from warnings and feature restrictions through to suspension and permanent termination. You'll be notified of any action and can appeal by emailing safety@nudr.app.",
      },
      {
        title: "What counts as a valid report?",
        body: "Every Red Flag must include a specific reason with evidence (screenshots, messages). Vague reports like 'I don't like them' are not accepted. Valid reasons include harassment, threats, sharing private photos, or dangerous behavior.",
      },
      {
        title: "How does this protect the community?",
        body: "This keeps the community respectful, safe, and mutually accountable. Abuse, harassment, or disrespect leads to permanent removal from NudR. False reporters also receive penalties.",
      },
    ],
  },
  {
    id: "faq",
    title: "Frequently Asked Questions",
    icon: "❓",
    content: [
      {
        title: "Why do I need to verify my age with ID?",
        body: "NudR is an adult platform. Age verification ensures all members are 18+, protecting both users and the integrity of our community. Your ID is processed securely and never stored on our servers after verification.",
      },
      {
        title: "What happens if I get reported?",
        body: "Each report is reviewed carefully by our safety team. Flags are applied after review, not automatically on report, which protects members from retaliatory reporting. Accumulated flags escalate enforcement, up to suspension or permanent termination.",
      },
      {
        title: "Can I attend events without being verified?",
        body: "No. All event attendees must have a verified account. This is non-negotiable and ensures every person at a NudR event is who they say they are.",
      },
      {
        title: "How do I block someone?",
        body: "Visit their profile, tap the three-dot menu in the top corner, and select 'Block'. They will no longer be able to see your profile, message you, or interact with you in any way.",
      },
      {
        title: "What is NudR+?",
        body: "NudR+ is our premium membership tier. It unlocks features like unlimited matches, priority event access, profile boosts, and advanced privacy controls.",
      },
      {
        title: "Is my data safe?",
        body: "Yes. NudR uses encryption for messages, never sells your data, and complies with GDPR. You can request a full data export or account deletion at any time from your settings.",
      },
      {
        title: "How do referrals work?",
        body: "Share your unique referral link with a friend. When they join and complete verification, you both receive NudR+ credits automatically within 24 hours.",
      },
    ],
  },
  {
    id: "security",
    title: "Security & Privacy",
    icon: "🔒",
    content: [
      {
        title: "End-to-End Encryption",
        body: "Every message you send is encrypted end-to-end using AES-256 — the same standard used by financial institutions and governments worldwide. Not even our own servers can read your conversations.",
      },
      {
        title: "Identity Verification",
        body: "Every profile goes through a real-time identity verification process before being activated. We use document scanning combined with liveness detection. No bots. No fake profiles.",
      },
      {
        title: "Data Privacy",
        body: "We never sell your data — to anyone, ever. Your personal information, location, and activity patterns are never shared with third-party advertisers. You can request a full export or permanent deletion at any time.",
      },
      {
        title: "Photo & Media Protection",
        body: "All photos and media you share are stored in encrypted containers. Screenshots within private chats are detected and the other party is notified immediately. Your images cannot be downloaded without your permission.",
      },
      {
        title: "Location Safety",
        body: "We never expose your precise GPS coordinates. We show only a generalised proximity — enough to help you find people nearby, never enough for anyone to track you.",
      },
      {
        title: "Reporting & Moderation",
        body: "Any profile or message can be reported with a single tap. Every report is reviewed by our safety team, prioritised by severity. Repeated or serious violations result in suspension or permanent termination.",
      },
    ],
  },
  {
    id: "hotlines",
    title: "Support Hotlines",
    icon: "📞",
    content: [
      {
        title: "Emergency Services",
        body: "911 (US) / 999 (UK) / 112 (EU) — Call immediately if you're in danger.",
      },
      {
        title: "Crisis Text Line",
        body: "Text HOME to 741741 — 24/7 free crisis support via text.",
      },
      {
        title: "Suicide Prevention Lifeline",
        body: "Call or text 988 — Available 24/7, confidential support.",
      },
      {
        title: "RAINN Sexual Assault Hotline",
        body: "1-800-656-4673 — 24/7 confidential support for sexual assault survivors.",
      },
      {
        title: "Domestic Violence Hotline",
        body: "1-800-799-7233 — Call, text, or chat, always available.",
      },
      {
        title: "Trevor Project (LGBTQ+)",
        body: "1-866-488-7386 — Crisis support for LGBTQ+ individuals.",
      },
      {
        title: "NudR Trust & Safety Team",
        body: "safety@nudr.app — Report in-app abuse directly to us.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    icon: "✉️",
    content: [
      {
        title: "General Support",
        body: "support@nudr.app — Our team usually responds within 24 hours.",
      },
      {
        title: "Safety & Abuse Reports",
        body: "safety@nudr.app — Report harassment, threats, or policy violations.",
      },
      {
        title: "Privacy & Data Requests",
        body: "privacy@nudr.app — For data exports, corrections, or account deletion.",
      },
    ],
  },
  {
    id: "values",
    title: "Our Values",
    icon: "❤️",
    content: [
      {
        title: "Respect first",
        body: "Every interaction should be respectful. Disrespect leads to removal.",
      },
      {
        title: "Consent always",
        body: "Nothing happens without clear, enthusiastic consent. Always ask first.",
      },
      {
        title: "No pressure, no harassment",
        body: "If someone says no or seems uncomfortable, stop immediately.",
      },
      {
        title: "Real people, real connections",
        body: "We verify everyone to keep the community authentic and safe.",
      },
      {
        title: "Safety over everything",
        body: "Every feature is built with safety as the #1 priority.",
      },
    ],
  },
];

function DropdownSection({ section, isOpen, onToggle }) {
  return (
    <div
      style={{
        borderRadius: T.radius,
        border: `1px solid ${isOpen ? T.borderHover : T.border}`,
        marginBottom: "12px",
        overflow: "hidden",
        transition: "all 0.2s",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "18px 22px",
          background: isOpen ? T.cardBg : "#ffffff",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          transition: "background 0.2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "22px" }}>{section.icon}</span>
          <span
            style={{
              fontSize: "17px",
              fontWeight: 500,
              color: T.black,
              letterSpacing: "-0.01em",
            }}
          >
            {section.title}
          </span>
        </div>
        <span
          style={{
            fontSize: "20px",
            color: T.light,
            transition: "transform 0.25s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ⌄
        </span>
      </button>

      <div
        style={{
          maxHeight: isOpen ? "1000px" : "0",
          overflow: "hidden",
          transition: "maxHeight 0.35s ease",
        }}
      >
        <div style={{ padding: "8px 22px 22px 22px" }}>
          {section.content.map((item, idx) => (
            <DropdownItem key={idx} title={item.title} body={item.body} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DropdownItem({ title, body }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      style={{
        borderBottom: `1px solid ${T.border}`,
        padding: "12px 0",
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          padding: "8px 0",
        }}
      >
        <span style={{ fontSize: "14.5px", fontWeight: 500, color: T.dark }}>
          {title}
        </span>
        <span
          style={{
            fontSize: "14px",
            color: T.light,
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ⌄
        </span>
      </button>
      <div
        style={{
          maxHeight: isOpen ? "200px" : "0",
          overflow: "hidden",
          transition: "maxHeight 0.25s ease",
        }}
      >
        <p
          style={{
            fontSize: "13.5px",
            lineHeight: 1.65,
            color: T.mid,
            padding: "8px 0 12px 0",
            margin: 0,
          }}
        >
          {body}
        </p>
      </div>
    </div>
  );
}

// Fake AI Summary Popup
function AISummaryPopup({ onClose }) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        animation: "fadeIn 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: T.radius,
          maxWidth: "400px",
          width: "90%",
          padding: "28px 26px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          animation: "slideUp 0.25s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <span style={{ fontSize: "26px" }}>✨</span>
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: T.light,
            }}
          >
            AI Summary
          </span>
        </div>

        <div
          style={{
            background: "#f5f5f5",
            borderRadius: "12px",
            padding: "18px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
          >
            <span style={{ fontSize: "14px" }}>🤖</span>
            <p
              style={{
                fontSize: "14px",
                lineHeight: 1.65,
                color: T.dark,
                margin: 0,
                fontStyle: "italic",
              }}
            >
              Analyzing all help content{dots}
            </p>
          </div>
        </div>

        <div
          style={{
            borderTop: `1px solid ${T.border}`,
            paddingTop: "18px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "12px",
              color: T.black,
            }}
          >
            📋 Quick Summary
          </h3>
          <ul
            style={{
              margin: 0,
              paddingLeft: "20px",
              fontSize: "13.5px",
              color: T.mid,
              lineHeight: 1.7,
            }}
          >
            <li>Verify your ID to access all features</li>
            <li>Red Flags trigger safety review and enforcement</li>
            <li>End-to-end encryption protects your messages</li>
            <li>24/7 support hotlines available for emergencies</li>
            <li>Contact support@nudr.app for help</li>
            <li>NudR+ gives unlimited matches & priority access</li>
          </ul>
        </div>

        <button
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: "24px",
            padding: "12px",
            background: T.black,
            color: "white",
            border: "none",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Got it
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default function HelpPage() {
  const [openSection, setOpenSection] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div
      style={{
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
        background: "#ffffff",
        color: T.black,
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Fixed AI button - top left corner */}
      <button
        onClick={() => setShowSummary(true)}
        style={{
          position: "fixed",
          bottom: "20px",
          left: "20px",
          width: "52px",
          height: "52px",
          borderRadius: "50%",
          background: T.black,
          border: "none",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 100,
          transition: "transform 0.2s, background 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.05)";
          e.currentTarget.style.background = T.dark;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.background = T.black;
        }}
      >
        <span style={{ fontSize: "24px" }}>✨</span>
      </button>

      {/* AI Summary Popup */}
      {showSummary && <AISummaryPopup onClose={() => setShowSummary(false)} />}

      <div
        style={{ maxWidth: "720px", margin: "0 auto", padding: "0 24px 80px" }}
      >
        {/* Simple header */}
        <div style={{ padding: "48px 0 32px", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: T.light,
              marginBottom: "20px",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: T.black,
              }}
            />
            NudR Help Center
          </div>
          <h1
            style={{
              fontSize: "clamp(32px, 6vw, 48px)",
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              color: T.black,
              marginBottom: "12px",
            }}
          >
            How can we help?
          </h1>
          <p
            style={{
              fontSize: "15px",
              color: T.mid,
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            Tap any section to expand. Everything is organized in simple
            dropdowns.
          </p>
        </div>

        {/* All dropdown sections */}
        {sections.map((section, idx) => (
          <DropdownSection
            key={section.id}
            section={section}
            isOpen={openSection === idx}
            onToggle={() => setOpenSection(openSection === idx ? null : idx)}
          />
        ))}

        {/* Simple footer */}
        <footer
          style={{
            textAlign: "center",
            padding: "48px 0 24px",
            borderTop: `1px solid ${T.border}`,
            marginTop: "24px",
          }}
        >
          <p style={{ fontSize: "12px", color: T.light }}>
            Need more help? Email{" "}
            <a
              href="mailto:support@nudr.app"
              style={{ color: T.black, textDecoration: "underline" }}
            >
              support@nudr.app
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
